import * as Sentry from "@sentry/react";

// Check if we're on a public page (not admin)
const isPublicPage = () => !window.location.pathname.startsWith('/admin');

export const initSentry = () => {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  
  // Only initialize if DSN is provided
  if (!dsn) {
    console.warn('Sentry DSN not configured. Error tracking disabled.');
    return;
  }

  // Build integrations - only include Replay on admin pages (heavy)
  const integrations: any[] = [Sentry.browserTracingIntegration()];
  
  // Only add Replay on admin pages to avoid impacting public page performance
  if (!isPublicPage()) {
    integrations.push(
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      })
    );
  }

  Sentry.init({
    dsn,
    environment: import.meta.env.MODE,
    integrations,
    
    // Performance monitoring - trace 100% in dev, 10% in production (reduced from 20%)
    tracesSampleRate: import.meta.env.MODE === 'development' ? 1.0 : 0.1,
    
    // Session Replay - only active on admin pages
    replaysSessionSampleRate: isPublicPage() ? 0 : 0.1,
    replaysOnErrorSampleRate: isPublicPage() ? 0 : 1.0,
    
    // Release tracking
    release: `vibe-moving@${import.meta.env.VITE_APP_VERSION || 'dev'}`,
    
    // Ignore common non-critical errors (self-healing or non-actionable)
    ignoreErrors: [
      'ResizeObserver loop limit exceeded',
      'Non-Error promise rejection captured',
      // Browser aborts in-flight promises when the user navigates away or closes the tab
      'AbortError',
      'The operation was aborted',
      'The user aborted a request',
      'browsing context is going away',
      'signal is aborted without reason',
      // In-app browser bridges (Instagram/Facebook/TikTok WebViews)
      /window\.webkit\.messageHandlers/,
      'sendDataToNative',
      'sendPageHideMessage',

      // Chunk load errors - these self-heal via page reload
      'ChunkLoadError',
      'Loading chunk',
      'Loading CSS chunk',
      /Importing a module script failed/,
      /Failed to fetch dynamically imported module/,
      /Unable to preload CSS/,
      // Network errors that are transient
      'Network request failed',
      'Failed to fetch',
      'Load failed',
      // Third-party script schema parsing errors (Google Tag Manager, extensions)
      '@context',
      /evaluating 'r\[.*\]\.toLowerCase/,
      // GTM/third-party pixel addEventListener on null (common in in-app browsers like TikTok/Instagram)
      "Cannot read properties of null (reading 'addEventListener')",
      "null is not an object (evaluating 'a.addEventListener')",
      // Mobile Safari / Google Translate DOM mutation conflicting with React reconciliation
      'The object can not be found here.',
      'NotFoundError',
      /removeChild.*Node/,
    ],
    
    // Filter events before sending to Sentry
    beforeSend(event, hint) {
      // Block on localhost if configured
      const blockLocal = import.meta.env.VITE_SENTRY_BLOCK_LOCAL === 'true';
      const localHosts = ['localhost', '127.0.0.1', '0.0.0.0'];

      if (blockLocal && localHosts.includes(window.location.hostname)) {
        console.warn('[Sentry] blocked on local host:', window.location.hostname, event, hint);
        return null;
      }
      
      // Filter chunk load errors that slip through ignoreErrors
      const errorMessage = event.exception?.values?.[0]?.value || '';
      const chunkErrorPatterns = [
        /chunk/i,
        /dynamically imported module/i,
        /module script failed/i,
        /preload/i,
      ];
      
      if (chunkErrorPatterns.some(pattern => pattern.test(errorMessage))) {
        return null;
      }
      
      // Filter third-party schema parsing errors
      if (errorMessage.includes('@context') || errorMessage.includes('toLowerCase')) {
        return null;
      }

      // Filter errors originating in third-party scripts (GTM, gtag, pixels)
      const frames = event.exception?.values?.[0]?.stacktrace?.frames || [];
      const thirdPartyFilenamePatterns = [
        /gtm\.js/i,
        /gtag\/js/i,
        /googletagmanager\.com/i,
        /google-analytics\.com/i,
        /connect\.facebook\.net/i,
        /analytics\.tiktok\.com/i,
        /static\.ads-twitter\.com/i,
      ];
      const isThirdPartyError = frames.some((f) =>
        thirdPartyFilenamePatterns.some((p) => p.test(f.filename || ''))
      );
      if (isThirdPartyError && errorMessage.includes('addEventListener')) {
        return null;
      }
      
      return event;
    },
  });

  console.info('[Sentry] initialized', {
    env: import.meta.env.MODE,
    hostname: window.location.hostname,
    dsnConfigured: !!dsn,
  });
};

// Set user context when logged in
export const setUserContext = (user: { id: string; email?: string | undefined }) => {
  Sentry.setUser({
    id: user.id,
    email: user.email || undefined,
  });
};

// Clear user context on logout
export const clearUserContext = () => {
  Sentry.setUser(null);
};

// Custom error logging with context
export const logError = (error: Error, context?: Record<string, any>) => {
  Sentry.captureException(error, {
    extra: context,
  });
};

// Custom event logging
export const logEvent = (message: string, level: 'info' | 'warning' | 'error' = 'info') => {
  Sentry.captureMessage(message, level);
};

// Add breadcrumb for tracking user actions
export const addBreadcrumb = (message: string, category: string, data?: Record<string, any>) => {
  Sentry.addBreadcrumb({
    category,
    message,
    level: 'info',
    data,
  });
};

// Set custom context
export const setContext = (key: string, context: Record<string, any>) => {
  Sentry.setContext(key, context);
};

// Set tags for filtering
export const setTag = (key: string, value: string) => {
  Sentry.setTag(key, value);
};

// Start a performance transaction
export const startTransaction = (name: string, op: string) => {
  return Sentry.startSpan(
    {
      name,
      op,
    },
    (span) => span
  );
};
